import React, { useState, useEffect, useRef } from 'react';
import { parseSheetDate } from './DataContext';

/** Stable YYYY-MM-DD for schedule rows (avoids Invalid Date from `Date(2025,0,15)` strings and TZ skew on ISO dates). */
function canonicalDateISO(item) {
  const iso = item.dateISO && String(item.dateISO).trim();
  if (iso && /^\d{4}-\d{2}-\d{2}/.test(iso)) return iso.slice(0, 10);
  const raw = item.date;
  if (raw === undefined || raw === null || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  const asStr = typeof raw === 'string' ? raw.trim() : String(raw);
  const parsed = parseSheetDate(asStr);
  const p = parsed && String(parsed).trim();
  if (p && /^\d{4}-\d{2}-\d{2}/.test(p)) return p.slice(0, 10);
  const d2 = new Date(asStr);
  if (Number.isNaN(d2.getTime())) return '';
  const y = d2.getFullYear();
  const m = String(d2.getMonth() + 1).padStart(2, '0');
  const day = String(d2.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function yearMonthFromItem(item) {
  const d = canonicalDateISO(item);
  return d ? d.slice(0, 7) : null;
}

function yearFromItem(item) {
  const d = canonicalDateISO(item);
  return d ? d.slice(0, 4) : null;
}

// Multi-select dropdown component with checkboxes
const MultiSelectDropdown = ({ label, options, selectedValues, onChange, className = '', hideLabel = false, displayFormat = null, searchable = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedArray = Array.isArray(selectedValues) ? selectedValues : (selectedValues ? [selectedValues] : []);

  const filteredOptions = searchable && searchQuery.trim()
    ? options.filter((option) =>
        String(formatDisplayValueForFilter(option, displayFormat))
          .toLowerCase()
          .includes(searchQuery.trim().toLowerCase())
      )
    : options;

  // Format display value
  const formatDisplayValue = (value) => {
    if (displayFormat && typeof displayFormat === 'function') {
      return displayFormat(value);
    }
    return value;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleToggleOption = (option) => {
    let newSelection;
    if (selectedArray.includes(option)) {
      newSelection = selectedArray.filter(item => item !== option);
    } else {
      newSelection = [...selectedArray, option];
    }
    onChange(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedArray.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {!hideLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          if (isOpen) setSearchQuery('');
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left bg-white flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm text-gray-700 truncate flex-1">
          {selectedArray.length === 0 
            ? hideLabel ? `All ${label}` : `All ${label}` 
            : selectedArray.length === 1 
              ? formatDisplayValue(selectedArray[0])
              : `${selectedArray.length} selected`}
        </span>
        <svg 
          className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'transform rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-gray-200 space-y-2">
            {searchable && (
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
            <button
              type="button"
              onClick={handleSelectAll}
              className="w-full text-left px-2 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
            >
              {selectedArray.length === options.length ? '✓ Deselect All' : '✓ Select All'}
            </button>
          </div>
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
          ) : (
            filteredOptions.map((option, index) => (
              <label
                key={index}
                className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedArray.includes(option)}
                  onChange={() => handleToggleOption(option)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                />
                <span className="text-sm text-gray-700">{formatDisplayValue(option)}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

function formatDisplayValueForFilter(value, displayFormat) {
  if (displayFormat && typeof displayFormat === 'function') {
    return displayFormat(value);
  }
  return value;
}

const FilterBar = ({ filters, onFilterChange, onDateRangeCommit, onReset, onExport = () => {}, onRefetch, data = [], filteredData = [], activeQuickFilter, setActiveQuickFilter, isReportPage = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDay, setCurrentDay] = useState(null);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const exportDropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setIsExportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Extract unique values for filter options
  const teachers = [
    '',
    ...[...new Set(data.map(item => item.teacher).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    ),
  ];
  const subjects = ['', ...new Set(data.map(item => item.subject).filter(Boolean))];
  const classes = ['', ...new Set(data.map(item => item.class).filter(Boolean))];
  const projectNames = ['', ...new Set(data.map(item => item.projectName).filter(Boolean))];

  // Extract unique months from data (YYYY-MM), using canonical ISO so options match string filters
  const months = Array.from(new Set(data.map(yearMonthFromItem).filter(Boolean))).sort();

  // Extract unique years from data
  const years = Array.from(new Set(data.map(yearFromItem).filter(Boolean))).sort((a, b) => b - a);

  // Helper to get month label
  const getMonthLabel = (ym) => {
    const [year, month] = ym.split('-');
    return `${new Date(year, month - 1).toLocaleString('default', { month: 'long' })} ${year}`;
  };

  // Function to get week range
  const getWeekRange = (offset = 0) => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + (offset * 7));
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    return { start: startOfWeek, end: endOfWeek };
  };

  // Function to format date for input
  const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** Keeps start/end + quick filter in one parent update (avoids broken month/year/week presets). */
  const commitDateRange = (startDate, endDate, activeQuickFilterKey) => {
    if (typeof onDateRangeCommit === 'function') {
      onDateRangeCommit({ startDate, endDate, activeQuickFilter: activeQuickFilterKey });
    } else {
      onFilterChange('startDate', startDate);
      onFilterChange('endDate', endDate);
      setActiveQuickFilter(activeQuickFilterKey ?? null);
    }
  };

  // Function to handle week filter - now works as continuous loop
  const handleWeekFilter = (offset, key) => {
    let newOffset;
    
    // Special case: "This Week" button should always reset to current week (offset 0)
    if (offset === 0) {
      newOffset = 0;
      console.log('This Week clicked - resetting to current week (offset 0)');
    } else {
      // For previous/next week, calculate from current state
      newOffset = currentWeekOffset + offset;
      console.log(`Week navigation: current offset ${currentWeekOffset} + ${offset} = ${newOffset}`);
    }
    
    // Update the current week offset state
    setCurrentWeekOffset(newOffset);
    
    // Get the week range for the new offset
    const { start, end } = getWeekRange(newOffset);
    console.log(`Week range for offset ${newOffset}:`, formatInputDate(start), 'to', formatInputDate(end));
    
    const qf =
      newOffset === -1 ? 'last' : newOffset === 0 ? 'this' : newOffset === 1 ? 'next' : 'weekNav';
    commitDateRange(formatInputDate(start), formatInputDate(end), qf);
    
    setCurrentDay(null);
  };

  // Function to handle today filter
  const handleTodayFilter = () => {
    if (activeQuickFilter === 'today') {
      commitDateRange('', '', null);
      setCurrentDay(null);
      setCurrentWeekOffset(0); // Reset week offset
      return;
    }
    const today = new Date();
    const formatted = formatInputDate(today);
    setCurrentDay(today);
    commitDateRange(formatted, formatted, 'today');
    setCurrentWeekOffset(0); // Reset week offset when selecting today
  };

  // Function to navigate to previous day
  const handlePreviousDay = () => {
    const baseDate = currentDay || new Date(); // Use current day or default to today
    const previousDay = new Date(baseDate);
    previousDay.setDate(baseDate.getDate() - 1);
    
    const formatted = formatInputDate(previousDay);
    setCurrentDay(previousDay);
    commitDateRange(formatted, formatted, 'dayNav');
    setCurrentWeekOffset(0); // Reset week offset when switching to day navigation
  };

  // Function to navigate to next day
  const handleNextDay = () => {
    const baseDate = currentDay || new Date(); // Use current day or default to today
    const nextDay = new Date(baseDate);
    nextDay.setDate(baseDate.getDate() + 1);
    
    const formatted = formatInputDate(nextDay);
    setCurrentDay(nextDay);
    commitDateRange(formatted, formatted, 'dayNav');
    setCurrentWeekOffset(0); // Reset week offset when switching to day navigation
  };

  // Handle multi-select months
  const handleMonthMultiSelect = (selectedMonthsArray) => {
    setSelectedMonths(selectedMonthsArray);
    
    if (selectedMonthsArray.length === 0) {
      commitDateRange('', '', null);
      return;
    }
    
    // Get earliest start and latest end from selected months
    const sortedMonths = [...selectedMonthsArray].sort();
    const firstMonth = sortedMonths[0].split('-');
    const lastMonth = sortedMonths[sortedMonths.length - 1].split('-');
    
    const start = new Date(parseInt(firstMonth[0], 10), parseInt(firstMonth[1], 10) - 1, 1);
    const lastDayLastMonth = new Date(parseInt(lastMonth[0], 10), parseInt(lastMonth[1], 10), 0);
    
    commitDateRange(formatInputDate(start), formatInputDate(lastDayLastMonth), 'multiMonth');
    setCurrentDay(null);
    setCurrentWeekOffset(0);
  };

  // Handle multi-select years
  const handleYearMultiSelect = (selectedYearsArray) => {
    setSelectedYears(selectedYearsArray);
    
    if (selectedYearsArray.length === 0) {
      commitDateRange('', '', null);
      return;
    }
    
    // Get earliest start and latest end from selected years
    const sortedYears = [...selectedYearsArray].map(y => parseInt(y, 10)).sort((a, b) => a - b);
    const start = new Date(sortedYears[0], 0, 1);
    const end = new Date(sortedYears[sortedYears.length - 1], 11, 31);
    
    commitDateRange(formatInputDate(start), formatInputDate(end), 'multiYear');
    setCurrentDay(null);
    setCurrentWeekOffset(0);
  };

  const handleFullReset = () => {
    setSelectedMonths([]);
    setSelectedYears([]);
    setCurrentDay(null);
    setCurrentWeekOffset(0);
    onReset();
  };

  // Calculate stats for filtered results (exclude rows where teacher or date is N/A)
  const filteredDataWithoutNA = filteredData.filter(item => item.teacher && item.teacher !== 'N/A' && item.date && item.date !== 'N/A');
  const originalDataWithoutNA = data.filter(item => item.teacher && item.teacher !== 'N/A' && item.date && item.date !== 'N/A');
  const stats = {
    totalClasses: filteredDataWithoutNA.length,
    uniqueTeachers: new Set(filteredDataWithoutNA.map(item => item.teacher).filter(Boolean)).size,
    uniqueSubjects: new Set(filteredDataWithoutNA.map(item => item.subject).filter(Boolean)).size,
    uniqueClasses: new Set(filteredDataWithoutNA.map(item => item.class).filter(Boolean)).size,
    totalOriginalData: originalDataWithoutNA.length,
    lateCount: filteredDataWithoutNA.filter(item => item.lateCount).length,
    routineTimeChange: filteredDataWithoutNA.filter(item => item.routineTimeChange).length,
  };

  // Export functions
  const exportToCSV = () => {
    let csvContent;
    
    if (isReportPage) {
      // For report page, export statistics
      const teacherStats = {};
      const classSubjectStats = {};
      
      filteredDataWithoutNA.forEach(item => {
        if (item.teacher) {
          teacherStats[item.teacher] = (teacherStats[item.teacher] || 0) + 1;
        }
        if (item.class && item.subject) {
          if (!classSubjectStats[item.class]) {
            classSubjectStats[item.class] = {};
          }
          classSubjectStats[item.class][item.subject] = 
            (classSubjectStats[item.class][item.subject] || 0) + 1;
        }
      });

      // Create CSV content for reports
      csvContent = [
        ['Report Type', 'Category', 'Value', 'Count'],
        ['Summary', 'Total Classes', '', filteredDataWithoutNA.length],
        ['Summary', 'Total Teachers', '', Object.keys(teacherStats).length],
        ['Summary', 'Total Subjects', '', new Set(filteredDataWithoutNA.map(item => item.subject).filter(Boolean)).size],
        ['Summary', 'Total Classes (Class Level)', '', new Set(filteredDataWithoutNA.map(item => item.class).filter(Boolean)).size],
        ['', '', '', ''],
        ['Teacher Statistics', 'Teacher Name', 'Class Count', ''],
        ...Object.entries(teacherStats).map(([teacher, count]) => ['', teacher, '', count]),
        ['', '', '', ''],
        ['Class & Subject Statistics', 'Class', 'Subject', 'Count'],
        ...Object.entries(classSubjectStats).flatMap(([className, subjects]) =>
          Object.entries(subjects).map(([subject, count]) => [className, subject, '', count])
        )
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    } else {
      // For schedule page, export schedule data
      csvContent = [
        ['Date', 'Time', 'Class', 'Subject', 'Chapter', 'Part', 'Topic', 'Teacher', 'Project Name'],
        ...filteredDataWithoutNA.map(item => [
          item.date || '',
          item.time || '',
          item.class || '',
          item.subject || '',
          item.chapter || '',
          item.part || '',
          item.topic || '',
          item.teacher || '',
          item.projectName || ''
        ])
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `afs_${isReportPage ? 'report' : 'schedule'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setIsExportDropdownOpen(false);
  };

  const exportToPDF = () => {
    let tableHtml;
    
    if (isReportPage) {
      // For report page, create statistics report
      const teacherStats = {};
      const classSubjectStats = {};
      
      filteredDataWithoutNA.forEach(item => {
        if (item.teacher) {
          teacherStats[item.teacher] = (teacherStats[item.teacher] || 0) + 1;
        }
        if (item.class && item.subject) {
          if (!classSubjectStats[item.class]) {
            classSubjectStats[item.class] = {};
          }
          classSubjectStats[item.class][item.subject] = 
            (classSubjectStats[item.class][item.subject] || 0) + 1;
        }
      });

      tableHtml = `
        <html>
          <head>
            <title>AFS Class Schedule Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              h1 { color: #333; text-align: center; }
              .stats { margin-bottom: 20px; }
              .stat-item { display: inline-block; margin-right: 20px; }
              .page-title { text-align: center; margin-bottom: 30px; }
              .section { margin-bottom: 30px; }
            </style>
          </head>
          <body>
            <div class="page-title">
              <h1>AFS Class Schedule Report</h1>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            
            <div class="section">
              <h2>Summary Statistics</h2>
              <div class="stats">
                <div class="stat-item"><strong>Total Classes:</strong> ${filteredDataWithoutNA.length}</div>
                <div class="stat-item"><strong>Teachers:</strong> ${Object.keys(teacherStats).length}</div>
                <div class="stat-item"><strong>Subjects:</strong> ${new Set(filteredDataWithoutNA.map(item => item.subject).filter(Boolean)).size}</div>
                <div class="stat-item"><strong>Classes:</strong> ${new Set(filteredDataWithoutNA.map(item => item.class).filter(Boolean)).size}</div>
              </div>
            </div>

            <div class="section">
              <h2>Classes Per Teacher</h2>
              <table>
                <thead>
                  <tr>
                    <th>Teacher</th>
                    <th>Class Count</th>
                  </tr>
                </thead>
                <tbody>
                  ${Object.entries(teacherStats).map(([teacher, count]) => `
                    <tr>
                      <td>${teacher}</td>
                      <td>${count}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2>Classes Per Class and Subject</h2>
              <table>
                <thead>
                  <tr>
                    <th>Class</th>
                    <th>Subject</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  ${Object.entries(classSubjectStats).flatMap(([className, subjects]) =>
                    Object.entries(subjects).map(([subject, count]) => `
                      <tr>
                        <td>${className}</td>
                        <td>${subject}</td>
                        <td>${count}</td>
                      </tr>
                    `)
                  ).join('')}
                </tbody>
              </table>
            </div>
          </body>
        </html>
      `;
    } else {
      // For schedule page, create schedule table
      tableHtml = `
        <html>
          <head>
            <title>AFS Class Schedule Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              h1 { color: #333; text-align: center; }
              .stats { margin-bottom: 20px; }
              .stat-item { display: inline-block; margin-right: 20px; }
              .page-title { text-align: center; margin-bottom: 30px; }
            </style>
          </head>
          <body>
            <div class="page-title">
              <h1>AFS Class Schedule Report</h1>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="stats">
              <div class="stat-item"><strong>Total Classes:</strong> ${filteredDataWithoutNA.length}</div>
              <div class="stat-item"><strong>Teachers:</strong> ${stats.uniqueTeachers}</div>
              <div class="stat-item"><strong>Subjects:</strong> ${stats.uniqueSubjects}</div>
              <div class="stat-item"><strong>Classes:</strong> ${stats.uniqueClasses}</div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Class</th>
                  <th>Subject</th>
                  <th>Chapter</th>
                  <th>Part</th>
                  <th>Topic</th>
                  <th>Teacher</th>
                  <th>Project Name</th>
                </tr>
              </thead>
              <tbody>
                ${filteredDataWithoutNA.map(item => `
                  <tr>
                    <td>${item.date || ''}</td>
                    <td>${item.time || ''}</td>
                    <td>${item.class || ''}</td>
                    <td>${item.subject || ''}</td>
                    <td>${item.chapter || ''}</td>
                    <td>${item.part || ''}</td>
                    <td>${item.topic || ''}</td>
                    <td>${item.teacher || ''}</td>
                    <td>${item.projectName || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(tableHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    setIsExportDropdownOpen(false);
  };

  const exportToJPG = () => {
    // For JPG export, we'll use html2canvas to capture the visible content
    import('html2canvas').then(html2canvas => {
      if (isReportPage) {
        // For report page, capture the statistics sections
        const reportContent = document.querySelector('.space-y-8');
        if (reportContent) {
          html2canvas.default(reportContent, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
          }).then(canvas => {
            const link = document.createElement('a');
            link.download = `afs_report_${new Date().toISOString().split('T')[0]}.jpg`;
            link.href = canvas.toDataURL('image/jpeg', 0.95);
            link.click();
          });
        } else {
          // Fallback: try to capture the main content area
          const mainContent = document.querySelector('main');
          if (mainContent) {
            html2canvas.default(mainContent, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff'
            }).then(canvas => {
              const link = document.createElement('a');
              link.download = `afs_report_${new Date().toISOString().split('T')[0]}.jpg`;
              link.href = canvas.toDataURL('image/jpeg', 0.95);
              link.click();
            });
          }
        }
      } else {
        // For schedule page, capture the schedule table
        let tableElement = document.querySelector('.bg-white.rounded-lg.shadow-md.overflow-x-auto table');
        if (!tableElement) {
          tableElement = document.querySelector('table');
        }
        
        if (tableElement) {
          html2canvas.default(tableElement, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
          }).then(canvas => {
            const link = document.createElement('a');
            link.download = `afs_schedule_${new Date().toISOString().split('T')[0]}.jpg`;
            link.href = canvas.toDataURL('image/jpeg', 0.95);
            link.click();
          });
        }
      }
    }).catch(() => {
      alert('JPG export requires additional libraries. Please use CSV or PDF export instead.');
    });
    setIsExportDropdownOpen(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      {/* Statistics Section */}
      <div className="mb-6 pb-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center flex-1 min-w-[140px]">
            <div className="text-2xl font-bold text-blue-600">{stats.totalClasses}</div>
            <div className="text-sm text-gray-600 mt-1">Total Filtered Classes</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center flex-1 min-w-[140px]">
            <div className="text-2xl font-bold text-green-600">{stats.uniqueTeachers}</div>
            <div className="text-sm text-gray-600 mt-1">Teachers</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center flex-1 min-w-[140px]">
            <div className="text-2xl font-bold text-purple-600">{stats.uniqueSubjects}</div>
            <div className="text-sm text-gray-600 mt-1">Subjects</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center flex-1 min-w-[140px]">
            <div className="text-2xl font-bold text-gray-700">{stats.totalOriginalData}</div>
            <div className="text-sm text-gray-600 mt-1">Total Available Classes</div>
          </div>
          {stats.lateCount > 0 && (
            <div className="bg-red-50 rounded-lg p-4 text-center flex-1 min-w-[140px] ring-1 ring-red-200">
              <div className="text-2xl font-bold text-red-600">{stats.lateCount}</div>
              <div className="text-sm text-gray-600 mt-1">Late Count</div>
            </div>
          )}
          {stats.routineTimeChange > 0 && (
            <div className="bg-red-50 rounded-lg p-4 text-center flex-1 min-w-[140px] ring-1 ring-red-200">
              <div className="text-2xl font-bold text-red-600">{stats.routineTimeChange}</div>
              <div className="text-sm text-gray-600 mt-1">Routine Time Change</div>
            </div>
          )}
        </div>
      </div>

      {/* Row 1: Teacher, Subject, Class, Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <MultiSelectDropdown
          label="Teacher"
          options={teachers.slice(1)}
          selectedValues={filters.teacher || []}
          onChange={(value) => onFilterChange('teacher', value)}
          searchable
        />
        <MultiSelectDropdown
          label="Subject"
          options={subjects.slice(1)}
          selectedValues={filters.subject || []}
          onChange={(value) => onFilterChange('subject', value)}
        />
        <MultiSelectDropdown
          label="Class"
          options={classes.slice(1)}
          selectedValues={filters.class || []}
          onChange={(value) => onFilterChange('class', value)}
        />
        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Range
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="w-full relative">
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => onFilterChange('startDate', e.target.value)}
                onMouseDown={(e) => {
                  const input = e.target;
                  if (input && input.showPicker) {
                    input.showPicker();
                  }
                }}
                max="2099-12-31"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
                placeholder="Start Date"
              />
            </div>
            <div className="w-full relative">
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => onFilterChange('endDate', e.target.value)}
                onMouseDown={(e) => {
                  const input = e.target;
                  if (input && input.showPicker) {
                    input.showPicker();
                  }
                }}
                max="2099-12-31"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
                placeholder="End Date"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Single Row: Quick Filters and Action Buttons */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Quick Filters */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Filters
          </label>
          
          {/* Mobile Layout - Stacked */}
          <div className="flex flex-col gap-3 lg:hidden">
            {/* Row 1: Week buttons */}
            <div className="flex justify-center gap-2">
              <button
                onClick={() => handleWeekFilter(-1, 'prev')}
                className={`px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${['last', 'weekNav'].includes(activeQuickFilter) ? 'bg-blue-800 ring-2 ring-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                title="Previous Week"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => handleWeekFilter(0, 'this')}
                className={`px-3 py-2 text-white text-sm rounded-md whitespace-nowrap transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${activeQuickFilter === 'this' ? 'bg-blue-800 ring-2 ring-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                <span className="block whitespace-nowrap">This Week</span>
              </button>
              <button
                onClick={() => handleWeekFilter(1, 'next')}
                className={`px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${['next', 'weekNav'].includes(activeQuickFilter) ? 'bg-blue-800 ring-2 ring-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                title="Next Week"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Row 2: Day navigation */}
            <div className="flex justify-center gap-2">
              <button
                onClick={handlePreviousDay}
                className="px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 bg-indigo-600 hover:bg-indigo-700"
                title="Previous Day"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={handleTodayFilter}
                className={`px-3 py-2 text-white text-sm rounded-md whitespace-nowrap transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${activeQuickFilter === 'today' ? 'bg-indigo-800 ring-2 ring-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                <span className="block whitespace-nowrap">Today</span>
              </button>
              <button
                onClick={handleNextDay}
                className="px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 bg-indigo-600 hover:bg-indigo-700"
                title="Next Day"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Row 3: Month, Year, and Project Name dropdowns */}
            <div className="flex justify-center gap-2">
              <div className="w-full max-w-[120px]">
                <MultiSelectDropdown
                  label="Month"
                  options={months}
                  selectedValues={selectedMonths}
                  onChange={handleMonthMultiSelect}
                  displayFormat={(ym) => ym ? getMonthLabel(ym) : ''}
                />
              </div>
              <div className="w-full max-w-[100px]">
                <MultiSelectDropdown
                  label="Year"
                  options={years}
                  selectedValues={selectedYears}
                  onChange={handleYearMultiSelect}
                />
              </div>
              <div className="w-full max-w-[120px]">
                <MultiSelectDropdown
                  label="Project"
                  options={projectNames.slice(1)}
                  selectedValues={filters.projectName || []}
                  onChange={(value) => onFilterChange('projectName', value)}
                />
              </div>
            </div>
          </div>
          
          {/* Desktop Layout - Single row with all elements */}
          <div className="hidden lg:flex lg:items-center lg:gap-2">
            <button
              onClick={() => handleWeekFilter(-1, 'prev')}
              className={`px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${['last', 'weekNav'].includes(activeQuickFilter) ? 'bg-blue-800 ring-2 ring-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
              title="Previous Week"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => handleWeekFilter(0, 'this')}
              className={`px-3 py-2 text-white text-sm rounded-md whitespace-nowrap transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${activeQuickFilter === 'this' ? 'bg-blue-800 ring-2 ring-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              <span className="block whitespace-nowrap">This Week</span>
            </button>
            <button
              onClick={() => handleWeekFilter(1, 'next')}
              className={`px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${['next', 'weekNav'].includes(activeQuickFilter) ? 'bg-blue-800 ring-2 ring-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
              title="Next Week"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
            <button
              onClick={handlePreviousDay}
              className="px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 bg-indigo-600 hover:bg-indigo-700"
              title="Previous Day"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleTodayFilter}
              className={`px-3 py-2 text-white text-sm rounded-md whitespace-nowrap transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${activeQuickFilter === 'today' ? 'bg-indigo-800 ring-2 ring-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            >
              <span className="block whitespace-nowrap">Today</span>
            </button>
            <button
              onClick={handleNextDay}
              className="px-3 py-2 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 bg-indigo-600 hover:bg-indigo-700"
              title="Next Day"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {/* Month and Year Filter Dropdowns */}
            <div className="min-w-[150px]">
              <MultiSelectDropdown
                label="Month"
                options={months}
                selectedValues={selectedMonths}
                onChange={handleMonthMultiSelect}
                hideLabel={true}
                displayFormat={(ym) => ym ? getMonthLabel(ym) : ''}
              />
            </div>
            <div className="min-w-[100px]">
              <MultiSelectDropdown
                label="Year"
                options={years}
                selectedValues={selectedYears}
                onChange={handleYearMultiSelect}
                hideLabel={true}
              />
            </div>
            <div className="min-w-[150px]">
              <MultiSelectDropdown
                label="Project"
                options={projectNames.slice(1)}
                selectedValues={filters.projectName || []}
                onChange={(value) => onFilterChange('projectName', value)}
                hideLabel={true}
              />
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
          <div className="flex items-center justify-center lg:justify-end gap-2 w-full lg:w-auto pt-6 lg:pt-6 flex-nowrap">
          <button
            type="button"
            onClick={handleFullReset}
            className="px-3 py-1.5 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 whitespace-nowrap flex-shrink-0"
          >
            Full Routine
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 whitespace-nowrap flex-shrink-0"
          >
            Cancel Class
          </button>
          <div className="relative flex-shrink-0" ref={exportDropdownRef}>
            <button
              onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center gap-1.5 whitespace-nowrap"
            >
              Export
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isExportDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                <div className="py-1">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export as CSV
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Export as PDF
                  </button>
                  <button
                    onClick={exportToJPG}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Export as JPG
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Class Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Cancel Class Request</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              <iframe 
                src="https://docs.google.com/forms/d/e/1FAIpQLSezaLOOUTNlVZdNtD9ikfy2zov8j28V3ANwZFzUn1CcT0Sdaw/viewform?embedded=true" 
                width="100%" 
                height="2672" 
                frameBorder="0" 
                marginHeight="0" 
                marginWidth="0"
                className="w-full"
                title="Cancel Class Form"
              >
                Loading…
              </iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar; 